/**
 * ImportModal.js
 * 
 * Universal drag-and-drop CSV/Excel importer.
 * Parses files on the client using Papa Parse (CSV) or SheetJS (Excel).
 * Both are loaded via CDN — zero npm installs.
 * 
 * Usage:
 *   <ImportModal
 *     title="Import Students"
 *     templateUrl="/templates/students.csv"
 *     columns={[
 *       { key:'full_name', label:'Full Name', required:true, example:'Aarav Singh' },
 *       { key:'email',     label:'Email',     required:true, example:'aarav@org.com' },
 *       { key:'password',  label:'Password',  required:false,example:'Student@123' },
 *     ]}
 *     onImport={async (rows) => { ... }}
 *     onClose={() => setShowModal(false)}
 *   />
 */

import React, { useState, useRef, useCallback } from 'react';

// ── Load SheetJS — CDN only (no npm package required) ────────────────────────
let xlsxCache = null;
const loadXlsx = () => new Promise((res, rej) => {
  if (xlsxCache) { res(xlsxCache); return; }
  if (window.XLSX) { xlsxCache = window.XLSX; res(xlsxCache); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
  s.onload  = () => { xlsxCache = window.XLSX; res(xlsxCache); };
  s.onerror = () => rej(new Error('Failed to load Excel parser. Please use CSV format instead.'));
  document.head.appendChild(s);
});

// ── Parse CSV manually (no Papa Parse needed for simple CSVs) ─────────────────
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  // Parse header — handle quoted fields
  const parseRow = (line) => {
    const result = [];
    let inQ = false, cur = '';
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; continue; }
      if (line[i] === ',' && !inQ) { result.push(cur.trim()); cur = ''; continue; }
      cur += line[i];
    }
    result.push(cur.trim());
    return result;
  };

  const headers = parseRow(lines[0]).map(h => h.replace(/^"|"$/g,'').trim());
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const values = parseRow(lines[i]);
    const obj = {};
    headers.forEach((h, j) => { obj[h] = (values[j] || '').toString().replace(/^"|"$/g,'').trim(); });
    rows.push(obj);
  }
  return rows;
}

// ── Parse Excel using SheetJS ─────────────────────────────────────────────────
async function parseExcel(file) {
  let XLSX;
  try {
    XLSX = await loadXlsx();
  } catch (err) {
    throw new Error('Could not load Excel parser. Please save your file as CSV (UTF-8) and upload that instead.');
  }
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, {
          type: 'array',
          codepage: 65001,  // Force UTF-8 codepage for Hindi/Devanagari
          raw: false,        // Parse values as strings, not raw types
        });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
        res(json);
      } catch (err) { rej(err); }
    };
    reader.onerror = rej;
    reader.readAsArrayBuffer(file);
  });
}

// ── Generate downloadable CSV template ───────────────────────────────────────
export function downloadCSV(filename, headers, exampleRows) {
  const lines = [
    headers.map(h => `"${h}"`).join(','),
    ...exampleRows.map(row => headers.map(h => `"${(row[h]||'').toString().replace(/"/g,'""')}"`).join(',')),
  ];
  // Add UTF-8 BOM (\uFEFF) so Excel opens the file with correct encoding for Hindi
  const bom  = '\uFEFF';
  const blob = new Blob([bom + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = {
    idle:    { color:'var(--text-muted)',    bg:'var(--bg-elevated)',         label:'Ready' },
    parsing: { color:'var(--accent-warning)',bg:'rgba(245,158,11,0.1)',       label:'Parsing...' },
    preview: { color:'var(--accent-primary)',bg:'rgba(79,142,247,0.1)',       label:'Preview' },
    uploading:{ color:'var(--accent-warning)',bg:'rgba(245,158,11,0.1)',      label:'Uploading...' },
    done:    { color:'var(--accent-success)',bg:'rgba(16,185,129,0.1)',       label:'Done' },
    error:   { color:'var(--accent-danger)', bg:'rgba(239,68,68,0.1)',        label:'Error' },
  };
  const c = cfg[status] || cfg.idle;
  return (
    <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:c.bg, color:c.color }}>
      {c.label}
    </span>
  );
}

// ── Main ImportModal ──────────────────────────────────────────────────────────
export function ImportModal({ title, columns, onImport, onClose, templateFilename, exampleRows }) {
  const [status,   setStatus]   = useState('idle');
  const [rows,     setRows]     = useState([]);
  const [errors,   setErrors]   = useState([]);
  const [result,   setResult]   = useState(null);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileRef = useRef();

  const requiredCols = columns.filter(c => c.required);

  // Validate rows against required columns
  const validateRows = (parsed) => {
    const validRows = [], errRows = [];
    parsed.forEach((row, i) => {
      const missing = requiredCols.filter(c => {
        // Check all possible keys for this column
        const keys = [c.key, c.label, ...(c.aliases || [])];
        return !keys.some(k => row[k]?.toString().trim());
      });
      if (missing.length) {
        errRows.push({ row: i+2, missing: missing.map(m => m.label), data: row });
      } else {
        // Normalize: map all possible column names to canonical key
        const normalized = {};
        columns.forEach(col => {
          const keys = [col.key, col.label, ...(col.aliases || [])];
          for (const k of keys) {
            if (row[k] !== undefined) { normalized[col.key] = row[k]; break; }
          }
        });
        validRows.push(normalized);
      }
    });
    return { validRows, errRows };
  };

  const processFile = useCallback(async (file) => {
    if (!file) return;
    setFileName(file.name);
    setStatus('parsing');
    setRows([]); setErrors([]); setResult(null);

    try {
      let parsed;
      if (file.name.match(/\.(xlsx|xls)$/i)) {
        parsed = await parseExcel(file);
      } else if (file.name.match(/\.csv$/i)) {
        // Read CSV with explicit UTF-8 encoding + BOM stripping
        // BOM (byte order mark) is added by Excel when saving as "CSV UTF-8"
        const text = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            let t = e.target.result;
            // Strip UTF-8 BOM if present (added by Excel "Save as CSV UTF-8")
            if (t.charCodeAt(0) === 0xFEFF) t = t.slice(1);
            res(t);
          };
          reader.onerror = rej;
          reader.readAsText(file, 'UTF-8');
        });
        parsed = parseCSV(text);
      } else {
        setStatus('error');
        setErrors([{ row: 0, missing: [], data: {}, msg: 'Unsupported file type. Use .csv, .xlsx, or .xls' }]);
        return;
      }

      if (parsed.length === 0) {
        setStatus('error');
        setErrors([{ msg: 'File is empty or has no data rows' }]);
        return;
      }

      const { validRows, errRows } = validateRows(parsed);
      setRows(validRows);
      setErrors(errRows);
      setStatus('preview');
    } catch (err) {
      setStatus('error');
      setErrors([{ msg: `Parse error: ${err.message}` }]);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleImport = async () => {
    if (rows.length === 0) return;
    setStatus('uploading');
    try {
      const res = await onImport(rows);
      setResult(res);
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrors([{ msg: err.response?.data?.error || err.message }]);
    }
  };

  const downloadTemplate = () => {
    const headers = columns.map(c => c.label);
    const examples = exampleRows || [
      columns.reduce((acc, c) => ({ ...acc, [c.label]: c.example || '' }), {}),
      columns.reduce((acc, c) => ({ ...acc, [c.label]: c.example2 || '' }), {}),
    ];
    downloadCSV(templateFilename || 'template.csv', headers, examples);
  };

  return (
    <div className="modal-overlay" style={{ zIndex:9999 }}>
      <div className="modal" style={{ maxWidth:680, maxHeight:'90vh', overflowY:'auto' }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h3 className="modal-title" style={{ margin:0 }}>📥 {title}</h3>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>
              Supports .csv, .xlsx, .xls files
            </div>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <StatusBadge status={status} />
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:22 }}>✕</button>
          </div>
        </div>

        {/* Template download */}
        <div style={{ padding:'12px 16px', background:'rgba(79,142,247,0.07)', borderRadius:8, border:'1px solid rgba(79,142,247,0.2)', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--accent-primary)', marginBottom:3 }}>📄 Download Template File</div>
            <div style={{ fontSize:12, color:'var(--text-muted)' }}>
              Required columns: {requiredCols.map(c => c.label).join(', ')}
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={downloadTemplate}
            style={{ color:'var(--accent-primary)', borderColor:'rgba(79,142,247,0.3)', flexShrink:0 }}>
            ⬇ Template CSV
          </button>
        </div>

        {/* Column reference */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)', marginBottom:8 }}>Column Reference</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {columns.map(col => (
              <span key={col.key} style={{
                padding:'3px 10px', borderRadius:20, fontSize:11, fontFamily:'var(--font-mono)',
                background: col.required ? 'rgba(79,142,247,0.12)' : 'var(--bg-elevated)',
                color: col.required ? 'var(--accent-primary)' : 'var(--text-muted)',
                border: `1px solid ${col.required ? 'rgba(79,142,247,0.3)' : 'var(--border)'}`,
              }}>
                {col.label} {col.required && '*'}
              </span>
            ))}
          </div>
        </div>

        {/* Drop zone */}
        {status !== 'done' && (
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? 'var(--accent-primary)' : 'var(--border)'}`,
              borderRadius: 10, padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
              background: dragging ? 'rgba(79,142,247,0.06)' : 'var(--bg-elevated)',
              transition: 'all 0.2s', marginBottom: 16,
            }}>
            <div style={{ fontSize:36, marginBottom:10 }}>📂</div>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:6 }}>
              {fileName ? `📄 ${fileName}` : 'Drop file here or click to browse'}
            </div>
            <div style={{ fontSize:12, color:'var(--text-muted)' }}>
              Supported: CSV, Excel (.xlsx, .xls) — max 5000 rows
            </div>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls"
              style={{ display:'none' }}
              onChange={e => processFile(e.target.files[0])} />
          </div>
        )}

        {/* Preview table */}
        {status === 'preview' && rows.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ fontSize:13, fontWeight:600 }}>
                Preview: {rows.length} valid row{rows.length!==1?'s':''}
                {errors.length > 0 && <span style={{ color:'var(--accent-danger)', marginLeft:8 }}>· {errors.length} with errors</span>}
              </div>
              <span style={{ fontSize:11, color:'var(--text-muted)' }}>Showing first 5</span>
            </div>
            <div style={{ overflowX:'auto', borderRadius:8, border:'1px solid var(--border)' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'var(--bg-card)' }}>
                    <th style={{ padding:'8px 12px', textAlign:'left', borderBottom:'1px solid var(--border)', color:'var(--text-muted)', fontWeight:600 }}>#</th>
                    {columns.map(c => (
                      <th key={c.key} style={{ padding:'8px 12px', textAlign:'left', borderBottom:'1px solid var(--border)', color:'var(--text-muted)', fontWeight:600 }}>
                        {c.label} {c.required && <span style={{ color:'var(--accent-primary)' }}>*</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0,5).map((row, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'7px 12px', color:'var(--text-muted)' }}>{i+1}</td>
                      {columns.map(c => (
                        <td key={c.key} style={{ padding:'7px 12px', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {row[c.key] || <span style={{ color:'var(--text-muted)' }}>—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {rows.length > 5 && (
                    <tr>
                      <td colSpan={columns.length+1} style={{ padding:'8px 12px', textAlign:'center', color:'var(--text-muted)', fontSize:12 }}>
                        ... and {rows.length - 5} more row{rows.length-5!==1?'s':''}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Error rows */}
        {errors.length > 0 && status !== 'done' && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--accent-danger)', marginBottom:6 }}>
              ⚠ {errors.length} row{errors.length!==1?'s':''} have issues (will be skipped):
            </div>
            <div style={{ maxHeight:120, overflowY:'auto' }}>
              {errors.slice(0,10).map((e, i) => (
                <div key={i} style={{ fontSize:11, padding:'4px 10px', background:'rgba(239,68,68,0.07)', borderRadius:6, marginBottom:4, color:'var(--accent-danger)' }}>
                  {e.msg || `Row ${e.row}: Missing ${e.missing?.join(', ')}`}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Result after import */}
        {status === 'done' && result && (
          <div style={{ marginBottom:16 }}>
            <div style={{ padding:'16px 18px', background:'rgba(16,185,129,0.08)', borderRadius:10, border:'1px solid rgba(16,185,129,0.25)', marginBottom:12 }}>
              <div style={{ fontWeight:700, fontSize:16, color:'var(--accent-success)', marginBottom:12 }}>✅ Import Complete!</div>
              <div style={{ display:'grid', gridTemplateColumns: result.enrolled !== undefined ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', gap:10 }}>
                {[
                  { label:'New Accounts', val:result.created, color:'var(--accent-success)' },
                  ...(result.enrolled !== undefined ? [{ label:'Enrolled', val:result.enrolled, color:'var(--accent-primary)' }] : []),
                  { label:'Already Enrolled', val:result.skipped, color:'var(--accent-warning)' },
                  { label:'Errors',  val:result.errors?.length||0, color:'var(--accent-danger)' },
                ].map(s => (
                  <div key={s.label} style={{ textAlign:'center', padding:'10px', background:'var(--bg-card)', borderRadius:8 }}>
                    <div style={{ fontSize:24, fontWeight:700, color:s.color }}>{s.val}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Clear messaging about what actually happened with enrollment */}
              {result.enrolled !== undefined && (
                result.enrolled > 0 ? (
                  <div style={{ marginTop:12, fontSize:12, color:'var(--accent-success)', fontWeight:600 }}>
                    ✓ {result.enrolled} student{result.enrolled!==1?'s':''} {result.examTitle ? <>enrolled in <strong>{result.examTitle}</strong></> : 'enrolled in the selected exam'}
                  </div>
                ) : (
                  <div style={{ marginTop:12, fontSize:12, color:'var(--accent-warning)' }}>
                    ⚠ 0 new enrollments — {result.skipped > 0 ? 'all listed students were already enrolled in this exam.' : 'check that students were enrolled correctly.'}
                  </div>
                )
              )}
              {result.enrolled === undefined && (
                <div style={{ marginTop:12, fontSize:12, color:'var(--text-muted)', padding:'8px 10px', background:'var(--bg-elevated)', borderRadius:6 }}>
                  ℹ️ No exam was selected for auto-enrollment — accounts were created/updated but not linked to any exam.
                </div>
              )}
            </div>

            {/* Skipped details — show WHY each row was skipped */}
            {result.skipped > 0 && result.skipped_details?.length > 0 && (
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--accent-warning)', marginBottom:6 }}>
                  ⚠ {result.skipped} record{result.skipped!==1?'s':''} already enrolled — reasons:
                </div>
                <div style={{ maxHeight:150, overflowY:'auto', border:'1px solid rgba(245,158,11,0.25)', borderRadius:8, background:'rgba(245,158,11,0.05)' }}>
                  {result.skipped_details.map((s, i) => (
                    <div key={i} style={{ display:'flex', gap:10, padding:'7px 12px', borderBottom: i < result.skipped_details.length-1 ? '1px solid rgba(245,158,11,0.1)' : 'none' }}>
                      <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--accent-warning)', flexShrink:0, minWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.email || s.row}</span>
                      <span style={{ fontSize:11, color:'var(--text-secondary)' }}>{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {result.errors?.length > 0 && (
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--accent-danger)', marginBottom:6 }}>
                  ❌ {result.errors.length} error{result.errors.length!==1?'s':''} — could not import:
                </div>
                <div style={{ maxHeight:120, overflowY:'auto', border:'1px solid rgba(239,68,68,0.25)', borderRadius:8, background:'rgba(239,68,68,0.05)' }}>
                  {result.errors.map((e, i) => (
                    <div key={i} style={{ display:'flex', gap:10, padding:'6px 12px', borderBottom: i < result.errors.length-1 ? '1px solid rgba(239,68,68,0.1)' : 'none' }}>
                      <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--accent-danger)', flexShrink:0, minWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.row}</span>
                      <span style={{ fontSize:11, color:'var(--text-secondary)' }}>{e.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          {status === 'done' ? (
            <button className="btn btn-primary" onClick={onClose}>Done ✓</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              {status === 'preview' && rows.length > 0 && (
                <button className="btn btn-primary" onClick={handleImport}>
                  📥 Import {rows.length} Row{rows.length!==1?'s':''}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
